/**
 * Overlap Detection Component
 *
 * Analyzes funds being compared against the user's portfolio
 * to detect overlaps and diversification opportunities.
 */

import { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useHoldings, type EnrichedHolding } from "@/hooks/useHoldings";
import { cn } from "@/lib/utils";
import type { Fund, FundCategory } from "@curie/shared";

// ============================================================================
// Types
// ============================================================================

interface OverlapDetectionProps {
  funds: Fund[];
  className?: string;
}

interface OverlapResult {
  type: "direct" | "category" | "diversifies";
  fund: Fund;
  existingHolding?: EnrichedHolding;
  message: string;
}

// ============================================================================
// Category Grouping Helper
// ============================================================================

/**
 * Groups categories that have similar exposure characteristics
 */
function getCategoryGroup(category: FundCategory): string {
  // Equity size-based categories
  if (category.startsWith("equity-large")) return "equity-large";
  if (category.startsWith("equity-mid")) return "equity-mid";
  if (category.startsWith("equity-small")) return "equity-small";
  if (category === "equity-flexi-cap" || category === "equity-multi-cap") {
    return "equity-diversified";
  }
  if (category === "equity-elss") return "equity-elss";
  if (category === "equity-sectoral") return "equity-sectoral";
  if (category === "equity-global") return "equity-global";

  // Debt categories grouped by duration
  if (category === "debt-liquid" || category === "debt-short-term") {
    return "debt-short";
  }
  if (category === "debt-medium-term") return "debt-medium";
  if (category === "debt-long-term" || category === "debt-gilt") {
    return "debt-long";
  }
  if (category === "debt-corporate") return "debt-corporate";

  // Hybrid categories
  if (category.startsWith("hybrid")) return "hybrid";

  return category;
}

// ============================================================================
// Overlap Analysis Logic
// ============================================================================

/**
 * Analyzes funds against user's holdings to detect overlaps
 */
function analyzeOverlaps(
  funds: Fund[],
  holdings: EnrichedHolding[]
): OverlapResult[] {
  const results: OverlapResult[] = [];

  // Create a map of ISINs in portfolio
  const portfolioIsins = new Set(holdings.map((h) => h.fundIsin));

  // Create a map of categories in portfolio with their holdings
  const portfolioCategories = new Map<string, EnrichedHolding>();
  for (const holding of holdings) {
    if (holding.fund?.category) {
      const categoryGroup = getCategoryGroup(holding.fund.category as FundCategory);
      // Store the first holding for each category group (for the message)
      if (!portfolioCategories.has(categoryGroup)) {
        portfolioCategories.set(categoryGroup, holding);
      }
    }
  }

  let hasAnyOverlap = false;

  for (const fund of funds) {
    // Check for direct overlap (same fund already in portfolio)
    if (portfolioIsins.has(fund.isin)) {
      hasAnyOverlap = true;
      const existingHolding = holdings.find((h) => h.fundIsin === fund.isin);
      results.push({
        type: "direct",
        fund,
        existingHolding,
        message: `${fund.name} is already in your portfolio`,
      });
      continue; // Skip category check if direct overlap
    }

    // Check for category overlap
    const fundCategoryGroup = getCategoryGroup(fund.category);
    if (portfolioCategories.has(fundCategoryGroup)) {
      hasAnyOverlap = true;
      const existingHolding = portfolioCategories.get(fundCategoryGroup);
      const existingFundName = existingHolding?.fund?.name || "an existing fund";
      results.push({
        type: "category",
        fund,
        existingHolding,
        message: `${fund.name} has similar exposure to ${existingFundName} in your portfolio`,
      });
    }
  }

  // If no overlaps found, add diversification message
  if (!hasAnyOverlap && funds.length > 0) {
    const firstFund = funds[0];
    if (firstFund) {
      results.push({
        type: "diversifies",
        fund: firstFund,
        message: "These funds would diversify your holdings",
      });
    }
  }

  return results;
}

// ============================================================================
// Message Item Component
// ============================================================================

interface OverlapMessageProps {
  result: OverlapResult;
}

const OVERLAP_MESSAGE_CONFIG = {
  direct: {
    icon: "⚠️",
    bgColor: "bg-red-50 dark:bg-red-950/30",
    borderColor: "border-red-200 dark:border-red-900",
    textColor: "text-red-800 dark:text-red-200",
  },
  category: {
    icon: "📊",
    bgColor: "bg-amber-50 dark:bg-amber-950/30",
    borderColor: "border-amber-200 dark:border-amber-900",
    textColor: "text-amber-800 dark:text-amber-200",
  },
  diversifies: {
    icon: "✅",
    bgColor: "bg-green-50 dark:bg-green-950/30",
    borderColor: "border-green-200 dark:border-green-900",
    textColor: "text-green-800 dark:text-green-200",
  },
} as const;

function OverlapMessage({ result }: OverlapMessageProps) {
  const { icon, bgColor, borderColor, textColor } = OVERLAP_MESSAGE_CONFIG[result.type];

  return (
    <div
      className={cn(
        "flex items-start gap-3 rounded-lg border p-3",
        bgColor,
        borderColor
      )}
    >
      <span className="text-lg shrink-0" role="img" aria-hidden="true">
        {icon}
      </span>
      <p className={cn("text-sm", textColor)}>{result.message}</p>
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

/**
 * OverlapDetection
 *
 * Displays overlap warnings or diversification messages based on
 * comparing funds against the user's current portfolio holdings.
 */
export function OverlapDetection({ funds, className }: OverlapDetectionProps) {
  const { data: holdingsData, isLoading, error } = useHoldings();

  const holdings = holdingsData?.holdings;
  const overlapResults = useMemo(() => {
    if (!holdings || funds.length === 0) {
      return [];
    }
    return analyzeOverlaps(funds, holdings);
  }, [funds, holdings]);

  // Loading state
  if (isLoading) {
    return <OverlapDetectionSkeleton className={className} />;
  }

  // Error state - silently fail without showing the component
  if (error) {
    return null;
  }

  // No funds to compare
  if (funds.length === 0) {
    return null;
  }

  // Empty portfolio case
  if (!holdings || holdings.length === 0) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle>Portfolio Analysis</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 p-3 dark:border-blue-900 dark:bg-blue-950/30">
            <span className="text-lg shrink-0" role="img" aria-hidden="true">
              ℹ️
            </span>
            <p className="text-sm text-blue-800 dark:text-blue-200">
              Add holdings to your portfolio to see overlap analysis with these funds.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // No results (shouldn't happen, but handle gracefully)
  if (overlapResults.length === 0) {
    return null;
  }

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle>Portfolio Overlap</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {overlapResults.map((result, index) => (
          <OverlapMessage key={`${result.type}-${result.fund.isin}-${index}`} result={result} />
        ))}
      </CardContent>
    </Card>
  );
}

// ============================================================================
// Skeleton Component
// ============================================================================

interface OverlapDetectionSkeletonProps {
  className?: string;
}

/**
 * Skeleton loader for OverlapDetection component
 */
export function OverlapDetectionSkeleton({ className }: OverlapDetectionSkeletonProps) {
  return (
    <Card className={className}>
      <CardHeader>
        <Skeleton className="h-6 w-40" />
      </CardHeader>
      <CardContent className="space-y-3">
        <Skeleton className="h-14 w-full rounded-lg" />
        <Skeleton className="h-14 w-full rounded-lg" />
      </CardContent>
    </Card>
  );
}
